import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LogService } from './log.service';
import { MessagesService } from './messages.service';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../environments/environment';
import { tap, catchError } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { of } from 'rxjs/observable/of';
import { KeyValuePair } from '../models/common/keyValuePair';

@Injectable()
export class ParameterService {
	private origin: string = environment.API_BASE_URL;

	constructor(
		private _http: HttpClient,
		private _logService: LogService,
		private _messagesService: MessagesService,
		private _cacheService: CacheService
	) { }

	getParameter<T>(code: string, applyCache: boolean = false, keepTime: number = 0, level?: number): Observable<T> {
		let url = `${this.origin}/api/parameters?parameterCode=${code}&level=`;
		if(level != null && level != undefined){
			url += level;
		}

		return this._http.get<T>(url)
			.pipe(
				tap(x => {
					this.log(`getParameter: id=${code}`)
					if (applyCache) {
						this._cacheService.add(code, x, keepTime);
					}
				}),
				catchError(this._logService.handleError<T>('getParameter'))
			);
	}

	getCachedParameter<T>(code: string, keepTime: number): Observable<T> {
		let value = this._cacheService.get(code);
		if (value != null) {
			return of(value);
		}

		return this.getParameter<T>(code, true, keepTime);
    }
    
    getCachedSystemParameter<T>(code: string, keepTime: number): Observable<T> {
		let value = this._cacheService.get(code);
		if (value != null) {
			return of(value);
		}

		return this.getParameter<T>(code, true, keepTime, 101);
	}

	getParameterArray<T>(code: string, applyCache: boolean = false, keepTime: number = 0, level?: number): Observable<T[]> {
        let url = `${this.origin}/api/parameters/array?parameterCode=${code}&level=`;
		if(level != null && level != undefined){
			url += level;
		}
        
        return this._http.get<T[]>(url)
			.pipe(
				tap(x => {
					this.log(`getParameterArray: id=${code}`)
					if (applyCache) {
						this._cacheService.add(code, x, keepTime);
					}
				}),
				catchError(this._logService.handleError<T[]>('getParameterArray'))
			);
	}

	getCachedParameterArray<T>(code: string, keepTime: number): Observable<T[]> {
		let value = this._cacheService.get(code);
		if (value != null) {
			return of(value);
		}

		return this.getParameterArray<T>(code, true, keepTime);
    }

    getCachedSystemParameterArray<T>(code: string, keepTime: number): Observable<T[]> {
		let value = this._cacheService.get(code);
		if (value != null) {
			return of(value);
		}

		return this.getParameterArray<T>(code, true, keepTime, 101);
	}

	getParameterObject<T>(code: string, applyCache: boolean = false, keepTime: number = 0, level?: number): Observable<T> {
		return this.getParameter<string>(code, false, 0, level)
					.map(x => <T> JSON.parse(x))
					.pipe(
						tap(x => {
							this.log(`getParameterObject: id=${code}`)
							if (applyCache) {
								this._cacheService.add(code, x, keepTime);
							}
						}),
						catchError(this._logService.handleError<T>('getParameterObject'))
					);
	}

	getCachedParameterObject<T>(code: string, keepTime: number): Observable<T> {
		let value = this._cacheService.get(code);
		if (value != null) {
			return of(value);
		}

		return this.getParameterObject<T>(code, true, keepTime);
	}

	searchParameterCode(searchText: string): Observable<KeyValuePair[]>{
		return this._http.get<KeyValuePair[]>(`${this.origin}/api/parameters?query=${searchText}`)
			.pipe(
				tap(x => {
					this.log(`searchParameterCode: id=${searchText}`)
				}),
				catchError(this._logService.handleError<KeyValuePair[]>('searchParameterCode'))
			);
	}

	updateParameter(paramCode: string, value: any, level: number): Observable<any>{
		return this._http.put<any>(`${this.origin}/api/parameters?parameterCode=${paramCode}&level=${level}`, JSON.stringify(value))
		.pipe(
			tap(x => {
				this.log(`saveParameter: code=${paramCode}, level=${level}`)
			}),
			catchError(this._logService.handleError<any>('saveParameter'))
		);
	} 

	private log(message: string) {
		this._messagesService.add('ParameterService: ' + message);
	}
}



// WEBPACK FOOTER //
// ./src/app/services/parameter.service.ts